import { useEffect, useState } from "react";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { GLASelector, GlaFilterPreset } from "../../../../../../components/specific/GLASelector";
import { FormDropdown } from "../../../../../../components/general/FormDropdown.tsx";
import { PrimaryButton } from "../../../../../../components/general/PrimaryButton.tsx";
import { SecondaryButton } from "../../../../../../components/general/SecondaryButton.tsx";
import { DEFAULT_GL_ACCOUNT, GlAccount } from "../../../../../../hooks/useGLAccounts.ts";
import { DOCUMENT_DIRECTION_LABELS } from "../../../../../../types/enums.ts";
import {
    SALES_VAT_CODE_OPTIONS,
    VatDirection,
    getDefaultDirection,
    getVatCodeOptions,
} from "../../../../../../types/vatCodes.ts";
import { BackendAPITypes } from "../../../../../../generated/blueprint.ts";
import { fieldLabelClasses, fieldVariants } from "../../../../../../components/general/fieldStyles.ts";
import { cn } from "../../../../../../lib/utils.ts";
import Checkbox from "../../../../../../components/general/Checkbox.tsx";
import type {VatCode} from "../../../../../../generated/enums.ts";

export type BookAwayDirection = VatDirection;

export type BookAwayPayload =
    BackendAPITypes.BACKENDPreHandleSyncApiV1CategorizationEngineExcludeTransactionFromMatchingPostRequestBody;

export interface TransactionToBookAway {
    id: string;
    generalLedgerAccountId?: string;
    relationName?: string;
    relationIBAN?: string;
    signedAmount: number;
}

const DIRECTION_OPTIONS: Array<{ value: BookAwayDirection; label: string }> = [
    {
        value: 'sales_invoice',
        label: DOCUMENT_DIRECTION_LABELS['sales_invoice'],
    },
    {
        value: 'purchase_invoice',
        label: DOCUMENT_DIRECTION_LABELS['purchase_invoice'],
    },
];

function getInitialGlAccount(
    generalLedgerAccountId: string | undefined,
    glAccounts: GlAccount[]
): GlAccount {
    const currentId = generalLedgerAccountId ?? DEFAULT_GL_ACCOUNT.id;
    return glAccounts.find((account) => account.id === currentId) ?? DEFAULT_GL_ACCOUNT;
}

export function BookAwayModal({
    transactionToBookAway,
    isOpen,
    onClose,
    onSubmit,
    isLoading,
    glAccounts,
    glAccountsLoading = false,
}: {
    transactionToBookAway: TransactionToBookAway | null;
    isOpen: boolean;
    onClose: () => void;
    onSubmit?: (payload: BookAwayPayload) => void;
    isLoading: boolean;
    glAccounts: GlAccount[];
    glAccountsLoading?: boolean;
}) {
    const [direction, setDirection] = useState<BookAwayDirection>(
        'sales_invoice'
    );
    const [vatCode, setVatCode] = useState<VatCode>(SALES_VAT_CODE_OPTIONS[0].value);
    const [bookWithVat, setBookWithVat] = useState<boolean>(false);
    const [selectedGla, setSelectedGla] = useState<GlAccount>(DEFAULT_GL_ACCOUNT);
    const [feedback, setFeedback] = useState<string>("");

    const disabled = glAccountsLoading || glAccounts.length === 0;
    const vatCodeOptions = getVatCodeOptions(direction);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const initialDirection = getDefaultDirection(transactionToBookAway?.signedAmount ?? 0);
        setDirection(initialDirection);
        setVatCode(getVatCodeOptions(initialDirection)[0].value);
        setBookWithVat(false);
        setSelectedGla(DEFAULT_GL_ACCOUNT);
        setFeedback("");
    }, [isOpen, transactionToBookAway]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setSelectedGla((current) =>
            current.id === DEFAULT_GL_ACCOUNT.id
                ? getInitialGlAccount(transactionToBookAway?.generalLedgerAccountId, glAccounts)
                : current
        );
    }, [isOpen, transactionToBookAway, glAccounts]);

    function handleDirectionChange(nextDirection: BookAwayDirection) {
        setDirection(nextDirection);
        setVatCode(getVatCodeOptions(nextDirection)[0].value);
    }

    function handleSubmit() {
        if (!transactionToBookAway) {
            return;
        }
        const isDefaultGla = selectedGla.id === DEFAULT_GL_ACCOUNT.id;
        onSubmit?.({
            transaction_id: transactionToBookAway.id,
            general_ledger_account_id: isDefaultGla ? null : selectedGla.id,
            feedback: feedback.trim(),
            vat_code: bookWithVat ? vatCode : null,
            direction: bookWithVat ? direction : null,
            subledger_id: null,
        });
    }

    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-40">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
            />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative transform overflow-visible rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                    >
                        <DialogTitle as="h3" className="text-base font-semibold text-gray-900">
                            Wegboeken
                        </DialogTitle>

                        <div className="mt-4">
                            <GLASelector
                                label="Grootboekrekening"
                                accounts={glAccounts}
                                value={selectedGla}
                                onChange={(account) => setSelectedGla(account ?? DEFAULT_GL_ACCOUNT)}
                                filterPreset={GlaFilterPreset.ALL}
                                documentDirectionFilter={direction}
                                placeholder="Categorie zoeken..."
                                disabled={disabled}
                            />
                        </div>

                        <div className="mt-4">
                            <Checkbox
                                value={bookWithVat}
                                onUpdateFunc={setBookWithVat}
                                label="Wegboeken met BTW"
                                className="text-sm text-gray-900"
                                disabled={disabled}
                            />
                        </div>

                        {bookWithVat && (
                            <>
                                <div className="mt-4">
                                    <label className={cn(fieldLabelClasses, "mb-1")}>Soort</label>
                                    <FormDropdown
                                        value={direction}
                                        onChange={(value) => handleDirectionChange(value as BookAwayDirection)}
                                        options={DIRECTION_OPTIONS}
                                        disabled={disabled}
                                    />
                                </div>

                                <div className="mt-4">
                                    <label className={cn(fieldLabelClasses, "mb-1")}>BTW code</label>
                                    <FormDropdown
                                        value={vatCode}
                                        onChange={(value) => setVatCode(value as VatCode)}
                                        options={vatCodeOptions}
                                        disabled={disabled}
                                    />
                                </div>
                            </>
                        )}

                        <div className="mt-4">
                            <label className={cn(fieldLabelClasses, "mb-1")} htmlFor="book-away-feedback">
                                Toelichting (optioneel)
                            </label>
                            <textarea
                                id="book-away-feedback"
                                rows={3}
                                value={feedback}
                                onChange={(e) => setFeedback(e.target.value)}
                                disabled={disabled}
                                className={cn(fieldVariants({ size: "multi" }), "px-3 py-1.5")}
                                placeholder="Optionele toelichting..."
                            />
                        </div>

                        <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                            <PrimaryButton
                                title={isLoading ? "Bezig..." : "Wegboeken"}
                                onClick={handleSubmit}
                                disabled={disabled || isLoading}
                                className="w-full justify-center sm:col-start-2"
                            />
                            <SecondaryButton
                                title="Terug"
                                onClick={onClose}
                                className="mt-3 w-full justify-center sm:col-start-1 sm:mt-0"
                            />
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
